import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ChatBubbleLeftEllipsisIcon, ArrowPathIcon, TrashIcon } from '@heroicons/react/24/outline';
import { bookmarkService } from '../services/bookmarkService';

// ── Status config ────────────────────────────────────────────────────────────
const STATUSES = [
  { value: 'unread',       label: 'Unread',       color: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300' },
  { value: 'acknowledged', label: 'Acknowledged',  color: 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400' },
  { value: 'planned',      label: 'Planned',       color: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400' },
  { value: 'wont_do',      label: "Won't do",      color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' },
  { value: 'done',         label: 'Done',          color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' },
];

const STATUS_MAP = Object.fromEntries(STATUSES.map(s => [s.value, s]));

const FILTERS = ['all', ...STATUSES.map(s => s.value)];

const StatusBadge = ({ status, onClick }) => {
  const s = STATUS_MAP[status] || STATUS_MAP.unread;
  return (
    <button
      type="button"
      onClick={onClick}
      title="Click to change status"
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium transition-opacity hover:opacity-75 ${s.color}`}
    >
      {s.label}
    </button>
  );
};

// Cycles to the next status in the list
function nextStatus(current) {
  const idx = STATUSES.findIndex(s => s.value === (current || 'unread'));
  return STATUSES[(idx + 1) % STATUSES.length].value;
}

// ── Main component ───────────────────────────────────────────────────────────
const AdminFeedback = () => {
  const [items, setItems] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async (token = null) => {
    token ? setLoadingMore(true) : setLoading(true);
    setError(null);
    try {
      const data = await bookmarkService.getFeedback({ nextToken: token });
      setItems(prev => token ? [...prev, ...(data.items || [])] : (data.items || []));
      setNextToken(data.nextToken || null);
    } catch {
      setError('Failed to load feedback. Please try again.');
    } finally {
      token ? setLoadingMore(false) : setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleStatusCycle = useCallback(async (item) => {
    const newStatus = nextStatus(item.status);
    // Optimistic update
    setItems(prev => prev.map(i => i.feedbackId === item.feedbackId ? { ...i, status: newStatus } : i));
    try {
      await bookmarkService.updateFeedback(item.feedbackId, item.userId, { status: newStatus });
    } catch {
      // Revert on failure
      setItems(prev => prev.map(i => i.feedbackId === item.feedbackId ? { ...i, status: item.status } : i));
    }
  }, []);

  const handleStatusSelect = useCallback(async (item, newStatus) => {
    if (newStatus === (item.status || 'unread')) return;
    setItems(prev => prev.map(i => i.feedbackId === item.feedbackId ? { ...i, status: newStatus } : i));
    try {
      await bookmarkService.updateFeedback(item.feedbackId, item.userId, { status: newStatus });
    } catch {
      setItems(prev => prev.map(i => i.feedbackId === item.feedbackId ? { ...i, status: item.status } : i));
    }
  }, []);

  const handleDelete = useCallback(async (item) => {
    if (!window.confirm('Delete this feedback? This cannot be undone.')) return;
    setItems(prev => prev.filter(i => i.feedbackId !== item.feedbackId));
    try {
      await bookmarkService.deleteFeedback(item.feedbackId, item.userId);
    } catch {
      // Restore on failure
      setItems(prev => {
        const idx = prev.findIndex(i => i.createdAt < item.createdAt);
        const copy = [...prev];
        copy.splice(idx === -1 ? copy.length : idx, 0, item);
        return copy;
      });
    }
  }, []);

  const filteredItems = useMemo(() => {
    if (statusFilter === 'all') return items;
    const target = statusFilter === 'unread' ? ['unread', undefined, null] : [statusFilter];
    return items.filter(i => target.includes(i.status || 'unread'));
  }, [items, statusFilter]);

  // Counts per status for filter tabs
  const counts = useMemo(() => {
    const c = { all: items.length };
    STATUSES.forEach(s => {
      c[s.value] = items.filter(i => (i.status || 'unread') === s.value).length;
    });
    return c;
  }, [items]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin" className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">User Feedback</h1>
        <button
          onClick={() => load()}
          disabled={loading}
          className="ml-auto text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-40"
          title="Refresh"
        >
          <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-400 mb-4">
          {error}
        </div>
      )}

      {/* Filter tabs */}
      {!loading && items.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-4">
          {FILTERS.map(f => (
            <button
              key={f}
              type="button"
              onClick={() => setStatusFilter(f)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors capitalize ${
                statusFilter === f
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {f === 'all' ? `All (${counts.all})` : `${STATUS_MAP[f]?.label} (${counts[f] || 0})`}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 animate-pulse">
              <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-1/4 mb-3" />
              <div className="h-4 bg-gray-100 dark:bg-gray-700 rounded w-3/4" />
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-gray-400 dark:text-gray-500">
          <ChatBubbleLeftEllipsisIcon className="h-12 w-12 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No feedback submitted yet.</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-12 text-gray-400 dark:text-gray-500">
          <p className="text-sm">No feedback with this status.</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {filteredItems.length} entr{filteredItems.length === 1 ? 'y' : 'ies'}
            {statusFilter !== 'all' && ` · ${STATUS_MAP[statusFilter]?.label}`}
            {nextToken && statusFilter === 'all' ? ' — more available' : ''}
          </p>
          <div className="space-y-3">
            {filteredItems.map(item => (
              <FeedbackCard
                key={item.feedbackId}
                item={item}
                onStatusCycle={handleStatusCycle}
                onStatusSelect={handleStatusSelect}
                onDelete={handleDelete}
              />
            ))}
          </div>
          {nextToken && statusFilter === 'all' && (
            <button
              onClick={() => load(nextToken)}
              disabled={loadingMore}
              className="mt-6 w-full py-2.5 text-sm font-medium text-primary-600 dark:text-primary-400 border border-primary-200 dark:border-primary-700 rounded-xl hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors disabled:opacity-50"
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          )}
        </>
      )}
    </div>
  );
};

// ── FeedbackCard ─────────────────────────────────────────────────────────────
const FeedbackCard = ({ item, onStatusCycle, onStatusSelect, onDelete }) => {
  const [showStatusMenu, setShowStatusMenu] = useState(false);

  const date = new Date(item.createdAt);
  const formatted = date.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  }) + ' ' + date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

  const currentStatus = item.status || 'unread';

  return (
    <div className={`bg-white dark:bg-gray-800 border rounded-xl p-5 transition-colors ${
      currentStatus === 'done' ? 'border-gray-100 dark:border-gray-700 opacity-60' : 'border-gray-200 dark:border-gray-700'
    }`}>
      <div className="flex items-start justify-between gap-4 mb-2">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{item.userEmail || item.userId}</span>
        <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0">{formatted}</span>
      </div>

      <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap mb-3">{item.note}</p>

      {item.page && item.page !== '/' && (
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">Page: {item.page}</p>
      )}

      {/* Actions row */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-50 dark:border-gray-700">
        <div className="relative">
          <StatusBadge
            status={currentStatus}
            onClick={() => setShowStatusMenu(v => !v)}
          />
          {showStatusMenu && (
            <div className="absolute left-0 top-6 z-10 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 min-w-32">
              {STATUSES.map(s => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => { onStatusSelect(item, s.value); setShowStatusMenu(false); }}
                  className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                    s.value === currentStatus ? 'font-semibold text-gray-900 dark:text-gray-100' : 'text-gray-600 dark:text-gray-300'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => onDelete(item)}
          className="text-gray-300 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 transition-colors"
          title="Delete feedback"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default AdminFeedback;
